import type { Injector } from '@angular/core';
import { EntityDataServiceRaw } from '@workbuddy/services/entities';
import { ConfirmServicePro } from '@workbuddy/ui/blocks/confirm';
import { requestContractorItemLine } from '../commands/contractor.item.row.commands';
import type { ContractorBulkCommand, ContractorBulkOutcome } from './contractor.item.delete.bulk.action';
import { resolveContractorBulkLines, runContractorBulkLines } from './contractor.item.bulk.tally';

export const CONTRACTOR_ITEM_REQUEST_BULK_ACTION = 'items.contractor.request';

/**
 * The contractor endpoint refuses the bulk pipeline, so each line goes through the same RequestItem gate as its row
 * action. Every ticked line is accounted for: one the subcontractor may no longer change is left out with the reason
 * the server stamped on it, grouped by reason, so a selection never shrinks silently (Hung 2026-10-08, WBS-12406).
 */
export async function handleContractorItemRequestBulkCommand(args: ContractorBulkCommand, injector: Injector, sorOnly: boolean, itemsChanged?: () => void): Promise<ContractorBulkOutcome> {

  if (args?.action !== CONTRACTOR_ITEM_REQUEST_BULK_ACTION) return { handled: false };

  const lines = resolveContractorBulkLines(args.data);
  if (!lines.length) return { handled: true };

  const noun = sorOnly ? 'SOR' : 'item';
  const eligible = lines.filter(line => line.capabilities?.canUpdate === true).length;
  const skipped = lines.length - eligible;
  if (eligible) {
    const question = `Request ${eligible} ${noun}${eligible === 1 ? '' : 's'}?${skipped ? ` ${skipped} of the ${lines.length} selected can't be requested — they are listed after.` : ''}`;
    const confirmed = await injector.get(ConfirmServicePro).confirm(sorOnly ? 'Request SORs' : 'Request items', question);
    if (!confirmed) return { handled: true };
  }

  const entityData = injector.get(EntityDataServiceRaw);

  await runContractorBulkLines(lines, injector, async line => {
    if (line.capabilities?.canUpdate !== true) return { kind: 'unchanged', reason: line.capabilities?.updateRefusal ?? "You can't change this line" };

    const refusal = await requestContractorItemLine(entityData, line);
    return refusal ? { kind: 'failed', reason: refusal.reason ?? 'Not requested' } : { kind: 'updated' };
  }, itemsChanged, { noun: `${noun}(s)` });

  return { handled: true, clearSelection: true };
}
